// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import ModifiedDropdown, {
  getAsValidRegex,
} from '@wandb/weave/common/components/elements/ModifiedDropdown';
// @todo: Migrate to 'Switch' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Switch} from '@wandb/weave/components';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useCallback, useMemo, useRef} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tab} from 'semantic-ui-react';
import {twMerge} from 'tailwind-merge';

import {SmoothingType, XAxisFormatValues} from '../../../generated/graphql';
import {type SettingType} from '../../../services/analytics/workspaceSettingsEvents';
import type * as QueryTypes from '../../../state/runs/types';
import {type RunHistoryKeyInfoKeys} from '../../../types/run';
import {type Line, prettyXAxisLabel} from '../../../util/plotHelpers';
import {
  useFullScreenLinePlotMaxRunsDynamicConfig,
  useLinePlotMaxRunsDynamicConfig,
} from '../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {
  useRampFlagFullFidelityRunsPerChart,
  useRampFlagIncreaseFullScreenMaxRuns,
  useStatsigGateCombinedMetricsRunsLimitUI,
} from '../../../util/rampFeatureFlags/linePlotFeatureFlags';
import * as RunHelpers from '../../../util/runhelpers';
import {MetricsPicker} from '../../elements/MetricsPicker';
import RangeInput from '../../elements/RangeInput';
import {useKeyInfoQueryContext} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {useIsFullScreenPanel} from '../../Panel/hooks/useIsFullScreenPanel';
import {usePanelActionsContext} from '../../Panel/PanelEditor/PanelEditor';
import {DebouncedInputOption} from '../../PanelChartOptions';
import {ResetSettingButton} from '../../WorkspaceDrawer/Settings/components/ResetSettingButton';
import {SettingIndentedRow} from '../../WorkspaceDrawer/Settings/components/SettingIndentedRow';
import {SettingLabel} from '../../WorkspaceDrawer/Settings/components/SettingLabel';
import {useTrackSettingsChange} from '../../WorkspaceDrawer/Settings/hooks/useTrackSettingEvent';
import {LinePlotConfigExcludeOutliers} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotConfigExcludeOutliers';
import {LinePlotPointAggregation} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotPointAggregation';
import {
  EMPTY_SMOOTHING_SETTINGS,
  LinePlotSmoothing,
} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotSmoothing';
import {getMaxRunsCeiling} from '../../WorkspaceDrawer/Settings/runLinePlots/utils/getCascadingMaxRuns';
import {
  XAxisFormat,
  xAxisIsBuiltin,
} from '../../WorkspaceDrawer/Settings/runLinePlots/XAxisFormat';
import {
  type DerivedPointVisualizationOption,
  isPointVizFullFidelity,
} from '../../WorkspaceDrawer/Settings/types';
import {defaultTitle} from '../common';
import {X_AXIS_LABELS} from '../defaults';
import {PlotTypeSelection} from '../edit/PlotTypeSelection';
import {usePanelConfigContext} from '../PanelConfigContext';
import {usePanelInteractionContext} from '../PanelInteractionContext';
import {usePanelZoom} from '../PanelZoomContext';
import {useProjectIsV2} from '../ProjectDetails';
import {usePanelGroupingSettings} from '../RunsLinePlotContext/usePanelGroupingSettings';
import {type RunsLinePlotConfig} from '../types';
import {isBarPlot, isEmptyChart} from '../utils/checkTypes';
import {usePanelZoom2} from '../zoom/PanelZoomContext2';
import {CombinedMetricsRunsUI} from './CombinedMetricsRunsUI';
import {EditMaxRuns} from './EditMaxRuns';
import {useXYChoices} from './useXYChoices';

type EditDataTabProps = {
  updateConfig: (config: RunsLinePlotConfig) => void;
  config: RunsLinePlotConfig;
  query: QueryTypes.QueryConfig;
  lines: Line[];
};

const DROPDOWN_FLEX_GROW_STYLE = {flexGrow: 1};
const NOOP_TRACK_SETTING = () => {};
const X_AXIS_DROPDOWN_RESULT_LIMIT = 100;

type XAxisDropdownOption = {
  icon: string;
  text: string;
  value: string;
  key: string;
};

function isNotMonotonicXAxis(keyInfoKeys: RunHistoryKeyInfoKeys, key: string) {
  return keyInfoKeys[key] != null && !keyInfoKeys[key].monotonic;
}

function getXAxisOption(
  key: string,
  keyInfoKeys: RunHistoryKeyInfoKeys,
  fromV2: boolean
) {
  const notMonotonic = isNotMonotonicXAxis(keyInfoKeys, key);
  let icon = 'arrow up';
  if (['_runtime', '_timestamp', '_absolute_runtime'].indexOf(key) > -1) {
    icon = 'calendar';
  } else if (notMonotonic) {
    icon = 'chart bar';
  }

  let text = X_AXIS_LABELS[key] || key;
  if (notMonotonic && !fromV2) {
    text += ' (Not Monotonically Increasing)';
  }

  return {
    icon,
    text,
    value: key,
    key,
  };
}

function getVisibleXAxisChoices({
  xAxisChoices,
  selectedXAxis,
  resultLimit,
}: {
  xAxisChoices: string[];
  selectedXAxis: string | undefined;
  resultLimit: number;
}) {
  const visibleChoices = xAxisChoices.slice(0, resultLimit + 1);
  if (selectedXAxis == null || visibleChoices.includes(selectedXAxis)) {
    return visibleChoices;
  }
  return [selectedXAxis, ...visibleChoices];
}

function getXAxisOptions(
  xAxisChoices: string[],
  keyInfoKeys: RunHistoryKeyInfoKeys,
  fromV2: boolean
) {
  return xAxisChoices.map(choice =>
    getXAxisOption(choice, keyInfoKeys, fromV2)
  );
}

function searchXAxisChoices({
  xAxisChoices,
  keyInfoKeys,
  fromV2,
  query,
}: {
  xAxisChoices: string[];
  keyInfoKeys: RunHistoryKeyInfoKeys;
  fromV2: boolean;
  query: string;
}) {
  if (query.trim().length === 0) {
    return [];
  }

  const regex = getAsValidRegex(query);
  const queryLower = query.toLowerCase();
  const matches: XAxisDropdownOption[] = [];
  const exactMatches: XAxisDropdownOption[] = [];

  for (const choice of xAxisChoices) {
    const option = getXAxisOption(choice, keyInfoKeys, fromV2);
    const optionText = option.text;
    const matchesQuery = regex
      ? regex.test(optionText)
      : optionText.toLowerCase().includes(queryLower);

    if (!matchesQuery) {
      continue;
    }

    if (optionText.toLowerCase() === queryLower) {
      exactMatches.push(option);
    } else {
      matches.push(option);
    }
  }

  return [...exactMatches, ...matches];
}

export const EditDataTab = ({
  updateConfig,
  config,
  query,
  lines,
}: EditDataTabProps) => {
  const {
    excludeOutliers,
    pointVisualizationMethod,
    smoothingType,
    smoothingWeight,
    xAxis,
    xAxisMax,
    xAxisMin,
    xLogScale,
    xAxisFormat,
    yLogScale,
    showOriginalAfterSmoothing,
  } = usePanelConfigContext();
  const {isSingleRun} = useSingleRunModeContext();
  const isBucketing = isPointVizFullFidelity(pointVisualizationMethod);
  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
  const canIncreaseFullScreenMaxRuns = useRampFlagIncreaseFullScreenMaxRuns();
  const dynamicConfigMaxRuns = useLinePlotMaxRunsDynamicConfig();
  const dynamicConfigFullScreenMaxRuns =
    useFullScreenLinePlotMaxRunsDynamicConfig();
  const {isFullScreen} = useIsFullScreenPanel();
  const useCombinedMetricsRunsLimitUI =
    useStatsigGateCombinedMetricsRunsLimitUI();
  const {handleConfigZoomChange, handleUserZoomChange} = usePanelZoom();
  const {zooming: zooming1} = usePanelInteractionContext();
  const {handleUserZoom, configZoom, zooming: zooming2} = usePanelZoom2();

  const trackPanelSettingsChange = useTrackSettingsChange();
  const {setRunOnApply} = usePanelActionsContext();

  /**
   * Conditional fns based on what kind of plot we're in. We're moving bucketing on to the new zoom context and we plan on deprecating the old one, but for now we're going to have to support both zoom contexts
   * - Bucketing: update the configZoom object directly
   * - Non-bucketing: use the handler from the context
   */
  const updateUserZoom = isBucketing ? handleUserZoomChange : handleUserZoom;
  const updateConfigZoom = isBucketing ? updateConfig : handleConfigZoomChange;
  const zooming = isBucketing ? zooming2 : zooming1;
  const renderingBarPlot = isBarPlot(lines, zooming);

  const {fromV2} = useProjectIsV2({
    entityName: query.entityName,
    projectName: query.projectName,
  });

  const settingsToTrack = useRef<Record<string, any>>({});

  const {
    keyInfoQuery: {historyKeyInfo: keyInfo},
  } = useKeyInfoQueryContext();
  const keyTypes = RunHelpers.useKeyTypes(keyInfo.keys);

  const {xAxisChoices, yAxisChoices} = useXYChoices({
    config,
    keyInfo,
    keyTypes,
    isSingleRun,
    xAxis,
  });

  const fromV2Project = Boolean(fromV2);
  const visibleXAxisChoices = useMemo(
    () =>
      getVisibleXAxisChoices({
        xAxisChoices,
        selectedXAxis: xAxis,
        resultLimit: X_AXIS_DROPDOWN_RESULT_LIMIT,
      }),
    [xAxisChoices, xAxis]
  );
  const xAxisOptions = useMemo(
    () => getXAxisOptions(visibleXAxisChoices, keyInfo.keys, fromV2Project),
    [fromV2Project, keyInfo.keys, visibleXAxisChoices]
  );
  const searchXAxisOptions = useCallback(
    (_options: any[], query: string) =>
      searchXAxisChoices({
        xAxisChoices,
        keyInfoKeys: keyInfo.keys,
        fromV2: fromV2Project,
        query,
      }),
    [fromV2Project, keyInfo.keys, xAxisChoices]
  );

  const metricsPickerConfig = useMemo(
    () => ({
      metricRegex: config.metricRegex,
      metrics: config.metrics,
      useMetricRegex: config.useMetricRegex,
    }),
    [config.metricRegex, config.metrics, config.useMetricRegex]
  );

  const {isAnyRunsetGrouped: isGrouped} = usePanelGroupingSettings();

  const handleXAxisTitleUpdate = useCallback(
    (value: string) => updateConfig({xAxisTitle: value}),
    [updateConfig]
  );

  const handleYAxisTitleUpdate = useCallback(
    (value: string) => updateConfig({yAxisTitle: value}),
    [updateConfig]
  );

  const handleChartTitleUpdate = useCallback(
    (value: string) => updateConfig({chartTitle: value}),
    [updateConfig]
  );

  const handleXAxisChange = useCallback(
    (_e: unknown, {value}: {value?: unknown}) => {
      if (typeof value !== 'string') {
        return;
      }

      const newConfigZoom: Partial<RunsLinePlotConfig> = {
        xAxisMin: undefined,
        xAxisMax: undefined,
      };
      const xAxisFormat =
        value !== config.xAxis && value === '_timestamp'
          ? XAxisFormatValues.Datetime
          : XAxisFormatValues.Number;
      if (!isBucketing) {
        updateConfig({
          xAxis: value,
          xAxisFormat,
        });
      } else {
        newConfigZoom.xAxis = value;
        newConfigZoom.xAxisFormat = xAxisFormat;
      }
      updateConfigZoom(newConfigZoom);
      updateUserZoom({
        xAxisMin: undefined,
        xAxisMax: undefined,
      });
    },
    [config.xAxis, isBucketing, updateConfig, updateConfigZoom, updateUserZoom]
  );

  const handleResetXAxis = useCallback(() => {
    updateConfig({
      xAxis: undefined,
      xAxisMin: undefined,
      xAxisMax: undefined,
      xLogScale: undefined,
      xAxisTitle: undefined,
      xAxisFormat: undefined,
    });
  }, [updateConfig]);

  const handleExcludeOutliersChange = useCallback(
    (areOutliersExcluded: boolean) => {
      settingsToTrack.current = {
        ...settingsToTrack.current,
        excludeOutliers: areOutliersExcluded
          ? 'exclude-outliers'
          : 'include-outliers',
      };

      setRunOnApply(() => () => {
        trackPanelSettingsChange({
          setting: 'excludeOutliers',
          settingValue: settingsToTrack.current?.excludeOutliers,
        });
      });
      updateConfig({
        excludeOutliers: areOutliersExcluded
          ? 'exclude-outliers'
          : 'include-outliers',
        // @ts-expect-error - ignoreOutliers is deprecated
        ignoreOutliers: undefined,
        showMinMaxOnHover: undefined,
      });
    },
    [setRunOnApply, trackPanelSettingsChange, updateConfig]
  );

  const handleClearPointVisualization = useCallback(() => {
    updateConfig({
      pointVisualizationMethod: undefined,
    });
  }, [updateConfig]);

  const pointVisualizationDisplayValues = useMemo(
    () => ({
      pointVisualizationMethod,
    }),
    [pointVisualizationMethod]
  );

  const pointVisualizationValues = useMemo(
    () => ({
      pointVisualizationMethod: config.pointVisualizationMethod,
    }),
    [config.pointVisualizationMethod]
  );

  const handlePointVisualizationMethodChange = useCallback(
    (value: DerivedPointVisualizationOption) => {
      settingsToTrack.current = {
        ...settingsToTrack.current,
        pointVisualizationMethod: value,
      };
      setRunOnApply(() => () => {
        trackPanelSettingsChange({
          setting: 'pointVisualizationMethod',
          settingValue:
            settingsToTrack.current?.pointVisualizationMethod?.toString(),
        });
      });

      const newRunsLimit = getMaxRunsCeiling({
        canOverrideMaxRuns,
        isBucketing: isPointVizFullFidelity(value),
        isFullScreen,
        canIncreaseFullScreenMaxRuns,
        dynamicConfigMaxRuns,
        dynamicConfigFullScreenMaxRuns,
      });

      updateConfig({
        pointVisualizationMethod: value,
        limit:
          config.limit != null && newRunsLimit
            ? Math.min(config.limit, newRunsLimit)
            : undefined, // don't need to set the limit if it wasn't already set
      });
    },
    [
      canIncreaseFullScreenMaxRuns,
      canOverrideMaxRuns,
      config.limit,
      dynamicConfigFullScreenMaxRuns,
      dynamicConfigMaxRuns,
      isFullScreen,
      setRunOnApply,
      trackPanelSettingsChange,
      updateConfig,
    ]
  );

  const smoothingDisplayValues = useMemo(
    () => ({
      smoothingWeight,
      smoothingType,
    }),
    [smoothingType, smoothingWeight]
  );

  const smoothingValues = useMemo(
    () => ({
      smoothingWeight: config.smoothingWeight,
      smoothingType: config.smoothingType,
    }),
    [config.smoothingType, config.smoothingWeight]
  );

  const handleTrackSmoothingSetting = useCallback(
    (setting: SettingType, value: string | undefined) => {
      trackPanelSettingsChange({
        setting,
        settingValue: value,
      });
    },
    [trackPanelSettingsChange]
  );

  const handleClearSmoothing = useCallback(() => {
    updateConfig(EMPTY_SMOOTHING_SETTINGS);
  }, [updateConfig]);

  const xAxisTitle = (
    <DebouncedInputOption
      value={config.xAxisTitle}
      update={handleXAxisTitleUpdate}
      placeholder={prettyXAxisLabel(xAxis, lines)}
    />
  );
  const yAxisTitle = (
    <DebouncedInputOption
      value={config.yAxisTitle}
      update={handleYAxisTitleUpdate}
    />
  );

  const xAxisRange = (
    <div>
      {!xAxisIsBuiltin(xAxis) ? (
        <Tailwind>
          <SettingIndentedRow className="ml-0">
            <div className="ml-12">
              <XAxisFormat
                trackSetting={NOOP_TRACK_SETTING}
                updateSetting={updateConfig}
                xAxisFormat={xAxisFormat}
              />
              <div className="flex min-w-[360px] items-center">
                <SettingLabel text="Range" className="ml-14 w-[87px]" />
                <RangeInput
                  disabled={
                    isEmptyChart(config) ||
                    (renderingBarPlot && xAxisMin == null && xAxisMax == null)
                  }
                  label="X Axis"
                  onMinChange={newVal => {
                    if (
                      (configZoom.xAxisMax ?? Infinity) < (newVal ?? -Infinity)
                    ) {
                      return;
                    }
                    const value = newVal ?? null;
                    if (isBucketing) {
                      updateConfig({
                        xAxisMin: value,
                      });
                    } else {
                      handleConfigZoomChange({
                        xAxisMin: value,
                      });
                    }
                  }}
                  onMaxChange={newVal => {
                    if (
                      (configZoom.xAxisMin ?? -Infinity) > (newVal ?? Infinity)
                    ) {
                      return;
                    }
                    const value = newVal ?? null;
                    if (isBucketing) {
                      updateConfig({
                        xAxisMax: value,
                      });
                    } else {
                      handleConfigZoomChange({
                        xAxisMax: value,
                      });
                    }
                  }}
                  minValue={configZoom.xAxisMin}
                  maxValue={configZoom.xAxisMax}
                  log={true}
                  logValue={xLogScale}
                  onLogChange={newVal => {
                    updateConfig({
                      xLogScale: newVal,
                    });
                  }}
                />
              </div>
            </div>
          </SettingIndentedRow>
        </Tailwind>
      ) : (
        <RangeInput
          disabled={
            isEmptyChart(config) ||
            (renderingBarPlot && xAxisMin == null && xAxisMax == null)
          }
          label="X Axis"
          onMinChange={newVal => {
            if ((configZoom.xAxisMax ?? Infinity) < (newVal ?? -Infinity)) {
              return;
            }
            const value = newVal ?? null;
            if (isBucketing) {
              updateConfig({
                xAxisMin: value,
              });
            } else {
              handleConfigZoomChange({
                xAxisMin: value,
              });
            }
          }}
          onMaxChange={newVal => {
            if ((configZoom.xAxisMin ?? -Infinity) > (newVal ?? Infinity)) {
              return;
            }
            const value = newVal ?? null;
            if (isBucketing) {
              updateConfig({
                xAxisMax: value,
              });
            } else {
              handleConfigZoomChange({
                xAxisMax: value,
              });
            }
          }}
          minValue={configZoom.xAxisMin}
          maxValue={configZoom.xAxisMax}
          log={true}
          logValue={xLogScale}
          onLogChange={newVal => {
            updateConfig({
              xLogScale: newVal,
            });
          }}
        />
      )}
    </div>
  );

  const yAxisRange = (
    <RangeInput
      disabled={isEmptyChart(config)}
      onMinChange={newVal => {
        if ((configZoom.yAxisMax ?? Infinity) < (newVal ?? -Infinity)) {
          return;
        }
        const value = newVal ?? null;
        if (isBucketing) {
          updateConfig({yAxisMin: value});
        } else {
          handleConfigZoomChange({yAxisMin: value});
        }
      }}
      onMaxChange={newVal => {
        if ((configZoom.yAxisMin ?? -Infinity) > (newVal ?? Infinity)) {
          return;
        }
        const value = newVal ?? null;
        if (isBucketing) {
          updateConfig({yAxisMax: value});
        } else {
          handleConfigZoomChange({yAxisMax: value});
        }
      }}
      minValue={configZoom.yAxisMin}
      maxValue={configZoom.yAxisMax}
      label="Y Axis"
      log
      logValue={yLogScale}
      onLogChange={newVal =>
        updateConfig({
          yLogScale: newVal,
        })
      }
    />
  );

  return (
    <Tab.Pane as="div" className="form-grid" data-test="edit-data-tab">
      <Tailwind>
        <div className="mt-8 flex items-center pb-16">
          <SettingLabel text="Chart title" />
          <DebouncedInputOption
            value={config.chartTitle}
            update={handleChartTitleUpdate}
            placeholder={defaultTitle(config)}
          />
        </div>
      </Tailwind>

      <Tailwind>
        <div
          className={twMerge('flex flex-col', 'gap-20 pb-16', 'h-full w-full')}>
          <div
            className={twMerge('flex flex-col', 'gap-8')}
            data-test="x-axis-setting">
            <div className="flex w-full items-center">
              <Tooltip
                trigger={<SettingLabel text="X axis" />}
                content="Use step, time or any variable logged with wandb.log as xaxis."
              />
              <div
                data-test="x-axis-select"
                className="items-center flex w-full flex-row gap-8">
                <ModifiedDropdown
                  allowRegexSearch
                  lazyLoad
                  options={xAxisOptions}
                  placeholder="X Axis"
                  resultLimit={X_AXIS_DROPDOWN_RESULT_LIMIT}
                  search={searchXAxisOptions}
                  selection
                  style={DROPDOWN_FLEX_GROW_STYLE}
                  value={xAxis}
                  onChange={handleXAxisChange}
                />
                <ResetSettingButton
                  isEnabled
                  onButtonClick={handleResetXAxis}
                  settingLevel="panel"
                />
              </div>
            </div>
            <div className="flex">
              <SettingIndentedRow>
                <div className="flex flex-col gap-8">
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Range" className="ml-14 w-[87px]" />
                      }
                      content="Fix the minimum and maximum values for the x-axis"
                    />
                    {xAxisRange}
                  </div>
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Label" className="ml-14 w-[87px]" />
                      }
                      content="Set the title for the x-axis"
                    />
                    <div className="flex">{xAxisTitle}</div>
                  </div>
                </div>
              </SettingIndentedRow>
            </div>
          </div>
        </div>
      </Tailwind>

      <Tailwind>
        <div
          className={twMerge('flex flex-col', 'gap-20 py-16', 'h-full w-full')}>
          <div className={twMerge('flex flex-col', 'gap-8')}>
            <div className="flex w-full items-start">
              <Tooltip
                trigger={<SettingLabel text="Y axis" />}
                content="Plot any numeric metric logged with wandb.log."
              />
              <div className="flex w-full flex-row items-start gap-8">
                <MetricsPicker
                  config={metricsPickerConfig}
                  keyInfo={keyInfo}
                  keyTypes={keyTypes}
                  options={yAxisChoices}
                  updateConfig={updateConfig}
                />
              </div>
            </div>
            <div className="flex">
              <SettingIndentedRow>
                <div className="flex flex-col gap-8">
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Range" className="ml-14 w-[87px]" />
                      }
                      content="Fix the minimum and maximum values for the y-axis"
                    />
                    {yAxisRange}
                  </div>
                  <div className="flex items-center">
                    <Tooltip
                      trigger={
                        <SettingLabel text="Label" className="ml-14 w-[87px]" />
                      }
                      content="Set the title for the y-axis"
                    />
                    <div className="flex">{yAxisTitle}</div>
                  </div>
                </div>
              </SettingIndentedRow>
            </div>
          </div>
        </div>
      </Tailwind>

      {!useCombinedMetricsRunsLimitUI && (
        <EditMaxRuns
          config={config}
          isGrouped={isGrouped}
          settingsToTrack={settingsToTrack.current}
          setRunOnApply={setRunOnApply}
          trackPanelSettingsChange={trackPanelSettingsChange}
          updateConfig={updateConfig}
        />
      )}

      {useCombinedMetricsRunsLimitUI && (
        <CombinedMetricsRunsUI
          config={config}
          trackPanelSettingsChange={trackPanelSettingsChange}
          updateConfig={updateConfig}
        />
      )}

      {isPointVizFullFidelity(pointVisualizationMethod) && (
        <TailwindContents>
          <LinePlotConfigExcludeOutliers
            excludeOutliers={excludeOutliers ?? 'include-outliers'}
            onExcludeOutliersChange={handleExcludeOutliersChange}
          />
        </TailwindContents>
      )}

      <TailwindContents>
        <LinePlotPointAggregation
          className={twMerge('group mt-12')}
          clearSetting={handleClearPointVisualization}
          displayValues={pointVisualizationDisplayValues}
          onPointVisualizationMethodChange={
            handlePointVisualizationMethodChange
          }
          settingLevel="panel"
          values={pointVisualizationValues}
        />
      </TailwindContents>

      <TailwindContents>
        <LinePlotSmoothing
          className={twMerge(
            'group my-12',
            smoothingType === SmoothingType.None ? 'mb-8' : ''
          )}
          displayValues={smoothingDisplayValues}
          settingLevel="panel"
          updateSetting={updateConfig}
          trackSetting={handleTrackSmoothingSetting}
          clearSetting={handleClearSmoothing}
          values={smoothingValues}
        />
      </TailwindContents>
      {smoothingWeight !== 0 && (isBucketing || !isGrouped) && (
        <Tailwind>
          <div className="mt-8 flex items-center">
            <Tooltip
              trigger={<SettingLabel text="Show Original" />}
              content="Show lines before smoothing"
            />
            <Switch.Root
              name="aggregate"
              size="medium"
              checked={showOriginalAfterSmoothing}
              onCheckedChange={checked => {
                updateConfig({
                  showOriginalAfterSmoothing: checked,
                });
                trackPanelSettingsChange({
                  setting: 'showOriginalAfterSmoothing',
                  settingValue: checked.toString(),
                });
              }}>
              <Switch.Thumb
                size="medium"
                checked={showOriginalAfterSmoothing}
              />
            </Switch.Root>
          </div>
        </Tailwind>
      )}
      {!renderingBarPlot && (
        <div data-test="plot-type-selection">
          <PlotTypeSelection
            config={config as RunsLinePlotConfig}
            updateConfig={updateConfig}
          />
        </div>
      )}
    </Tab.Pane>
  );
};
